/**
 * Helpers for the per-user case planning (CasePlan) day lists.
 *
 * `plannedFor` is a calendar day ("YYYY-MM-DD") interpreted in LOCAL time —
 * all helpers here build/compare days locally so there are no UTC off-by-one
 * surprises around midnight.
 */

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const WEEKDAYS_LONG = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

const MONTHS_LONG = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

/** Local "YYYY-MM-DD" for a Date (defaults to now). */
export function localDayIso(date = new Date()) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')

  return `${y}-${m}-${d}`
}

/** Today as "YYYY-MM-DD" (local). */
export function todayIso() {
  return localDayIso(new Date())
}

/** Parse "YYYY-MM-DD" into a local Date (midnight). */
export function isoToDate(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)

  return new Date(y, (m || 1) - 1, d || 1)
}

/** Shift an ISO day by `n` days, returning a new ISO day. */
export function addDaysIso(iso, n) {
  const date = isoToDate(iso)

  date.setDate(date.getDate() + n)

  return localDayIso(date)
}

/** Whole-day difference `iso - baseIso` (positive = future). */
export function dayDiff(iso, baseIso = todayIso()) {
  const ms = isoToDate(iso).getTime() - isoToDate(baseIso).getTime()

  return Math.round(ms / 86400000)
}

/** Inclusive list of ISO days from `startIso` to `endIso`. */
export function buildDayRange(startIso, endIso) {
  const out = []
  if (!startIso || !endIso) return out

  let cursor = startIso
  let guard = 0
  while (cursor <= endIso && guard < 400) {
    out.push(cursor)
    cursor = addDaysIso(cursor, 1)
    guard++
  }

  return out
}

/** Monday (local, midnight) of the week containing `date`. */
function mondayOf(date) {
  const d = new Date(date)
  const wd = (d.getDay() + 6) % 7 // 0 = Mon … 6 = Sun

  d.setDate(d.getDate() - wd)
  d.setHours(0, 0, 0, 0)

  return d
}

/**
 * Describe a day for chip/badge rendering, relative to today.
 * relative: 'overdue' | 'today' | 'tomorrow' | 'upcoming'
 * weekOffset: calendar-week distance from today's week (0 = this week,
 * 1 = next week, 2 = the one after, …; negative = past weeks).
 */
export function describeDay(iso, baseIso = todayIso()) {
  const date = isoToDate(iso)
  const diff = dayDiff(iso, baseIso)
  const weekday = WEEKDAYS[date.getDay()]
  const weekdayLong = WEEKDAYS_LONG[date.getDay()]
  const dayNum = date.getDate()
  const monthShort = MONTHS[date.getMonth()]
  const monthLong = MONTHS_LONG[date.getMonth()]
  const isWeekend = date.getDay() === 0 || date.getDay() === 6

  const weekOffset = Math.round(
    (mondayOf(date) - mondayOf(isoToDate(baseIso))) / (7 * 86400000),
  )

  let relative = 'upcoming'
  if (diff < 0) relative = 'overdue'
  else if (diff === 0) relative = 'today'
  else if (diff === 1) relative = 'tomorrow'

  let label
  if (relative === 'today') label = 'Today'
  else if (relative === 'tomorrow') label = 'Tomorrow'
  else label = `${weekday} ${dayNum}`

  return {
    iso,
    weekday,
    weekdayLong,
    dayNum,
    monthShort,
    monthLong,
    isWeekend,
    diff,
    weekOffset,
    relative,
    label,
  }
}

/** Short badge label for a case's planned day, e.g. "Today" / "Mon, May 22". */
export function planBadgeLabel(iso, baseIso = todayIso()) {
  if (!iso) return ''
  const d = describeDay(iso, baseIso)
  if (d.relative === 'today') return 'Today'
  if (d.relative === 'tomorrow') return 'Tomorrow'

  return `${d.weekday}, ${d.monthShort} ${d.dayNum}`
}

/** Forward window (calendar days) for the upcoming-day buttons.
 *  Total visible days incl. Today = `PLAN_FORWARD_DAYS + 1`. */
export const PLAN_FORWARD_DAYS = 14

/**
 * Does a case match a plan filter? `filter` is one of:
 *   { kind: 'all' } | { kind: 'day', iso } | { kind: 'future' }
 * Uses the current user's `case.myPlan.plannedFor`. (Every active case is
 * planned now — there is no "unplanned" filter.)
 */
export function caseMatchesPlanFilter(
  caseItem,
  filter,
  baseIso = todayIso(),
  forwardDays = PLAN_FORWARD_DAYS,
) {
  const kind = filter?.kind ?? 'all'
  if (kind === 'all') return true

  const iso = caseItem?.myPlan?.plannedFor ?? null
  if (kind === 'day') return !!iso && iso === filter.iso
  if (kind === 'future') return !!iso && iso > addDaysIso(baseIso, forwardDays)

  return true
}

/**
 * The plan-filter bucket a case currently lives in, by its own `myPlan`:
 *   planned beyond the forward window → Future; otherwise → that specific day.
 *   A case with no plan for the viewer (edge — e.g. not created/owned by them)
 *   → "All", so it stays reachable. Inverse of `caseMatchesPlanFilter`.
 */
export function planFilterForCase(
  caseObj,
  baseIso = todayIso(),
  forwardDays = PLAN_FORWARD_DAYS,
) {
  const iso = caseObj?.myPlan?.plannedFor ?? null
  if (!iso) return { kind: 'all', iso: null }
  if (iso > addDaysIso(baseIso, forwardDays)) return { kind: 'future', iso: null }

  return { kind: 'day', iso }
}

/**
 * Bucket the loaded cases into the filter-bar structure (all client-side).
 * Returns:
 *   {
 *     all, future: { count },
 *     overdueDays: [{ ...describeDay, count }],   // only days with cases, oldest→newest
 *     today: { ...describeDay, count },
 *     upcomingDays: [{ ...describeDay, count }],   // today+1..+window; weekdays always, weekends only if count>0
 *   }
 * Cases with a plan land in exactly one day/future bucket. (Cases with no plan
 * for the viewer aren't bucketed — every active case is planned now.)
 */
export function buildPlanBuckets(
  cases,
  baseIso = todayIso(),
  forwardDays = PLAN_FORWARD_DAYS,
) {
  const windowEnd = addDaysIso(baseIso, forwardDays)
  const list = cases || []

  let todayCount = 0
  let futureCount = 0
  const overdueMap = new Map()
  const upcomingMap = new Map()

  for (const c of list) {
    const iso = c?.myPlan?.plannedFor ?? null
    if (!iso) {
      continue
    } else if (iso < baseIso) {
      overdueMap.set(iso, (overdueMap.get(iso) || 0) + 1)
    } else if (iso === baseIso) {
      todayCount++
    } else if (iso <= windowEnd) {
      upcomingMap.set(iso, (upcomingMap.get(iso) || 0) + 1)
    } else {
      futureCount++
    }
  }

  const overdueDays = [...overdueMap.keys()]
    .sort()
    .map(iso => ({ ...describeDay(iso, baseIso), count: overdueMap.get(iso) }))

  const upcomingDays = []
  for (const iso of buildDayRange(addDaysIso(baseIso, 1), windowEnd)) {
    const d = describeDay(iso, baseIso)
    const count = upcomingMap.get(iso) || 0
    if (d.isWeekend && count === 0) continue
    upcomingDays.push({ ...d, count })
  }

  return {
    all: list.length,
    overdueDays,
    today: { ...describeDay(baseIso, baseIso), count: todayCount },
    upcomingDays,
    future: { count: futureCount },
  }
}

/**
 * Group agenda items (each with `plannedFor`) into the dashboard buckets:
 *   { overdue: [], today: [], tomorrow: [], upcomingByDay: [{ ...describeDay, items }] }
 * Overdue is flat (oldest first, as the server returns them); upcoming is
 * grouped per day.
 */
export function groupAgenda(items, baseIso = todayIso()) {
  const tomorrowIso = addDaysIso(baseIso, 1)
  const overdue = []
  const today = []
  const tomorrow = []
  const upcomingMap = new Map()

  for (const it of items || []) {
    const iso = it?.plannedFor
    if (!iso) continue
    if (iso < baseIso) {
      overdue.push(it)
    } else if (iso === baseIso) {
      today.push(it)
    } else if (iso === tomorrowIso) {
      tomorrow.push(it)
    } else {
      const arr = upcomingMap.get(iso) ?? []

      arr.push(it)
      upcomingMap.set(iso, arr)
    }
  }

  const upcomingByDay = [...upcomingMap.keys()]
    .sort()
    .map(iso => ({ ...describeDay(iso, baseIso), items: upcomingMap.get(iso) }))

  return { overdue, today, tomorrow, upcomingByDay }
}
